import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DirectionsBus as BusIcon,
  Person as PersonIcon,
  Euro as EuroIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { it, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Club {
  record_id: string;
  club_name: string;
}

interface DriverStat {
  pilot_id: string;
  pilot_name: string;
  pilot_nickname: string;
  total_trips: number;
  total_passengers: number;
  total_revenue: number;
  average_occupancy_rate: number;
  most_frequent_route: string | null;
}

interface ClubReport {
  club_id: string;
  club_name: string;
  report_period_start: string;
  report_period_end: string;
  total_shuttles: number;
  total_schedules: number;
  total_bookings: number;
  total_passengers: number;
  total_revenue: number;
  average_price_per_seat: number;
  average_occupancy_rate: number;
  busiest_day: string | null;
  most_popular_route: string | null;
  driver_stats: DriverStat[];
}

interface DriverReport {
  schedule_id: string;
  departure_date: string;
  departure_time: string;
  route: string;
  driver_name: string;
  driver_nickname: string;
  capacity: number;
  seats_booked: number;
  occupancy_rate: number;
  price_per_seat: number | null;
  total_revenue: number;
  status: string;
}

const ShuttleReports = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState(new Date());
  const [clubReport, setClubReport] = useState<ClubReport | null>(null);
  const [driverReports, setDriverReports] = useState<Record<string, DriverReport[]>>({});
  const [expandedDrivers, setExpandedDrivers] = useState<Record<string, boolean>>({});
  
  const currentLocale = i18n.language === 'it' ? it : enGB;

  const fetchUserClubs = useCallback(async () => {
    try {
      const response = await api.get('/me/clubs');
      // Handle both single club object and array of clubs
      const clubsData = Array.isArray(response.data) ? response.data : [response.data];
      setClubs(clubsData);
      if (clubsData.length === 1) {
        setSelectedClub(clubsData[0].record_id);
      }
    } catch (err) {
      setError(t('shuttleReports.failedToFetchClubs'));
      console.error('Error fetching clubs:', err);
    }
  }, [t]);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setInitialLoading(true);
        await fetchUserClubs();
      } finally {
        setInitialLoading(false);
      }
    };
    initializeComponent();
  }, [fetchUserClubs]);

  const fetchClubReport = async () => {
    if (!selectedClub) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/shuttle-reports/club/${selectedClub}`, {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setClubReport(response.data);
      setDriverReports({});
      setExpandedDrivers({});
    } catch (err) {
      setError(t('shuttleReports.failedToFetchReport'));
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverReport = async (driverId: string) => {
    setLoading(true);
    try {
      const response = await api.get(`/shuttle-reports/driver/${driverId}`, {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setDriverReports(prev => ({
        ...prev,
        [driverId]: response.data
      }));
    } catch (err) {
      console.error('Error fetching driver report:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDriverExpansion = async (driverId: string) => {
    const isExpanded = expandedDrivers[driverId];
    
    if (!isExpanded && !driverReports[driverId]) {
      await fetchDriverReport(driverId);
    }
    
    setExpandedDrivers(prev => ({
      ...prev,
      [driverId]: !isExpanded
    }));
  };

  const downloadReport = () => {
    if (!clubReport) return;

    const csvContent = generateCSV(clubReport);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `shuttle_report_${clubReport.club_name}_${format(startDate, 'dd-MM-yyyy')}_${format(endDate, 'dd-MM-yyyy')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateCSV = (report: ClubReport) => {
    let csv = `${t('shuttleReports.title')}\n`;
    csv += `Club,${report.club_name}\n`;
    csv += `${t('shuttleReports.reportPeriod')},${format(new Date(report.report_period_start), 'dd/MM/yyyy')} - ${format(new Date(report.report_period_end), 'dd/MM/yyyy')}\n\n`;
    
    csv += `${t('shuttleReports.reportSummary')}\n`;
    csv += `${t('shuttleReports.totalShuttles')},${report.total_shuttles}\n`;
    csv += `Total Schedules,${report.total_schedules}\n`;
    csv += `Total Bookings,${report.total_bookings}\n`;
    csv += `${t('shuttleReports.totalPassengers')},${report.total_passengers}\n`;
    csv += `${t('shuttleReports.totalRevenue')},€${report.total_revenue}\n`;
    csv += `${t('shuttleReports.averagePricePerSeat')},€${report.average_price_per_seat}\n`;
    csv += `${t('shuttleReports.occupancyRate')},${report.average_occupancy_rate.toFixed(1)}%\n`;
    csv += `${t('shuttleReports.busiestDay')},${report.busiest_day || t('shuttleReports.na')}\n`;
    csv += `${t('shuttleReports.mostPopularRoute')},${report.most_popular_route || t('shuttleReports.na')}\n\n`;
    
    csv += `${t('shuttleReports.driverPerformance')}\n`;
    csv += `${t('shuttleReports.driver')},Nickname,${t('shuttleReports.trips')},${t('shuttleReports.passengers')},${t('shuttleReports.revenue')},${t('shuttleReports.occupancyRate')},${t('shuttleReports.mostFrequentRoute')}\n`;
    
    report.driver_stats.forEach((driver: DriverStat) => {
      csv += `${driver.pilot_name},${driver.pilot_nickname},${driver.total_trips},${driver.total_passengers},€${driver.total_revenue},${driver.average_occupancy_rate.toFixed(1)}%,${driver.most_frequent_route || t('shuttleReports.na')}\n`;
    });
    
    return csv;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (initialLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AssessmentIcon sx={{ mr: 2 }} />
          {t('shuttleReports.title')}
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <TextField
                select
                fullWidth
                label={t('shuttleReports.selectClub')}
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                disabled={clubs.length === 0}
                helperText={clubs.length === 0 ? t('shuttleReports.noClubsAvailable') : ""}
              >
                {clubs.length === 0 ? (
                  <MenuItem value="">{t('shuttleReports.noClubsAvailable')}</MenuItem>
                ) : (
                  clubs.map((club) => (
                    <MenuItem key={club.record_id} value={club.record_id}>
                      {club.club_name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <DatePicker
                label={t('shuttleReports.startDate')}
                value={startDate}
                onChange={(newValue: any) => newValue && setStartDate(newValue instanceof Date ? newValue : newValue?.toDate ? newValue.toDate() : new Date())}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <DatePicker
                label={t('shuttleReports.endDate')}
                value={endDate}
                onChange={(newValue: any) => newValue && setEndDate(newValue instanceof Date ? newValue : newValue?.toDate ? newValue.toDate() : new Date())}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={fetchClubReport}
                disabled={!selectedClub || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
              >
                {t('shuttleReports.generateReport')}
              </Button>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {clubReport && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <BusIcon /> {t('shuttleReports.totalShuttles')}
                    </Typography>
                    <Typography variant="h5">
                      {clubReport.total_shuttles}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <PersonIcon /> {t('shuttleReports.totalPassengers')}
                    </Typography>
                    <Typography variant="h5">
                      {clubReport.total_passengers}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <EuroIcon /> {t('shuttleReports.totalRevenue')}
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(clubReport.total_revenue)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      {t('shuttleReports.occupancyRate')}
                    </Typography>
                    <Typography variant="h5">
                      {clubReport.average_occupancy_rate.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('shuttleReports.reportSummary')}</Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadReport}
                >
                  {t('shuttleReports.downloadCSV')}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('shuttleReports.reportPeriod')}
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(clubReport.report_period_start), 'dd/MM/yyyy')} to {format(new Date(clubReport.report_period_end), 'dd/MM/yyyy')}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('shuttleReports.averagePricePerSeat')}
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(clubReport.average_price_per_seat)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('shuttleReports.busiestDay')}
                  </Typography>
                  <Typography variant="body1">
                    {clubReport.busiest_day || t('shuttleReports.na')}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('shuttleReports.mostPopularRoute')}
                  </Typography>
                  <Typography variant="body1">
                    {clubReport.most_popular_route || t('shuttleReports.na')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('shuttleReports.driverPerformance')}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('shuttleReports.driver')}</TableCell>
                      <TableCell align="center">{t('shuttleReports.trips')}</TableCell>
                      <TableCell align="center">{t('shuttleReports.passengers')}</TableCell>
                      <TableCell align="right">{t('shuttleReports.revenue')}</TableCell>
                      <TableCell align="center">{t('shuttleReports.occupancy')}</TableCell>
                      <TableCell>{t('shuttleReports.mostFrequentRoute')}</TableCell>
                      <TableCell align="center">{t('shuttleReports.details')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clubReport.driver_stats.map((driver) => (
                      <React.Fragment key={driver.pilot_id}>
                        <TableRow>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{driver.pilot_name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                @{driver.pilot_nickname}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{driver.total_trips}</TableCell>
                          <TableCell align="center">{driver.total_passengers}</TableCell>
                          <TableCell align="right">{formatCurrency(driver.total_revenue)}</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${driver.average_occupancy_rate.toFixed(1)}%`}
                              color={driver.average_occupancy_rate > 70 ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {driver.most_frequent_route || t('shuttleReports.na')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => toggleDriverExpansion(driver.pilot_id)}
                              size="small"
                            >
                              {expandedDrivers[driver.pilot_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                            <Collapse in={expandedDrivers[driver.pilot_id]} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                  {t('shuttleReports.tripDetails')} {driver.pilot_name}
                                </Typography>
                                {driverReports[driver.pilot_id] ? (
                                  <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>{t('shuttleReports.date')}</TableCell>
                                          <TableCell>{t('shuttleReports.time')}</TableCell>
                                          <TableCell>{t('shuttleReports.route')}</TableCell>
                                          <TableCell align="center">{t('shuttleReports.seats')}</TableCell>
                                          <TableCell align="center">{t('shuttleReports.occupancy')}</TableCell>
                                          <TableCell align="right">{t('shuttleReports.revenue')}</TableCell>
                                          <TableCell>{t('shuttleReports.status')}</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {driverReports[driver.pilot_id].map((trip) => (
                                          <TableRow key={trip.schedule_id}>
                                            <TableCell>{format(new Date(trip.departure_date), 'dd/MM/yyyy')}</TableCell>
                                            <TableCell>{trip.departure_time}</TableCell>
                                            <TableCell>{trip.route}</TableCell>
                                            <TableCell align="center">
                                              {trip.seats_booked}/{trip.capacity}
                                            </TableCell>
                                            <TableCell align="center">
                                              {trip.occupancy_rate.toFixed(0)}%
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrency(trip.total_revenue)}
                                            </TableCell>
                                            <TableCell>
                                              <Chip 
                                                label={trip.status}
                                                size="small"
                                                color={trip.status === 'completed' ? 'success' : 'default'}
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <CircularProgress size={20} />
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ShuttleReports;